import { proxyCustomElement, HTMLElement, createEvent, forceUpdate, h, Host } from '@stencil/core/internal/client';
import { c as cl } from './clsx.m.js';
import { c as createTypeValidator, a as createLogger, i as isTriggerEvent, e as emitEvent, l as logDeprecatedEventOnce, g as getCSSValue } from './utils.js';
import { d as defineCustomElement$3 } from './cl-animation2.js';
import { d as defineCustomElement$2 } from './cl-icon2.js';

// Constants
const component = 'cl-accordion';
const accordionConstants = {
  ALIGNMENT: {
    LEFT: 'left',
    SPACE_BETWEEN: 'space-between',
    SPACE_AROUND: 'space-around',
  },
};
// Type-validators
const accordionValidator = {
  alignment: createTypeValidator(accordionConstants.ALIGNMENT),
};

const clAccordionCss = ".sc-cl-accordion-h{--accordion-spacing-top-bottom:var(--spacing-1, 0.5rem);--accordion-chevron-size:var(--size-3, 1.5rem);--accordion-chevron-color:rgb(var(--font-color-onlight-default, 20, 20, 20));--accordion-header-color:rgb(var(--font-color-onlight-default, 20, 20, 20));display:block;padding-bottom:var(--accordion-spacing-top-bottom);transition:padding-bottom var(--animation-duration-fast, 150ms);width:100%}.sc-cl-accordion-h,.sc-cl-accordion-h *.sc-cl-accordion,.sc-cl-accordion-h *.sc-cl-accordion::before,.sc-cl-accordion-h *.sc-cl-accordion::after,.sc-cl-accordion-h .sc-cl-accordion-s>*,.sc-cl-accordion-h .sc-cl-accordion-s *,.sc-cl-accordion-h .sc-cl-accordion-s *::before,.sc-cl-accordion-h .sc-cl-accordion-s *::after{box-sizing:border-box}.sc-cl-accordion-h .sc-cl-accordion-s>[slot=label]{padding-right:var(--spacing-1, 0.5rem)}.sc-cl-accordion-h .accordion__header.sc-cl-accordion{--typography-color:var(--accordion-header-color);align-items:center;border:var(--border-width, 0.125rem) solid transparent;border-radius:var(--border-radius-interactive-small, 0.875rem);cursor:pointer;display:flex;outline:0;padding:var(--accordion-spacing-top-bottom) 0;position:relative}.sc-cl-accordion-h .accordion__header.sc-cl-accordion .accordion__icon.sc-cl-accordion{--icon-color:var(--accordion-chevron-color)}.sc-cl-accordion-h .accordion__header--space-between.sc-cl-accordion .accordion__animation.sc-cl-accordion{margin-left:auto}.sc-cl-accordion-h .accordion__header--left.sc-cl-accordion{justify-content:flex-start}.sc-cl-accordion-h .accordion__header--space-around.sc-cl-accordion{margin:0 calc(var(--spacing-1, 0.5rem) + var(--spacing-half, 0.25rem))}.sc-cl-accordion-h .accordion__header--space-around.sc-cl-accordion .accordion__animation.sc-cl-accordion{margin-left:auto}.sc-cl-accordion-h .accordion__header--primary.sc-cl-accordion{--accordion-chevron-color:rgb(var(--font-color-onlight-brand, 125, 38, 204));--accordion-header-color:rgb(var(--font-color-onlight-brand, 125, 38, 204))}.sc-cl-accordion-h .accordion__header.sc-cl-accordion:hover{--accordion-chevron-color:rgb(var(--font-color-onlight-brand, 125, 38, 204));--accordion-header-color:rgb(var(--font-color-onlight-brand, 125, 38, 204))}.sc-cl-accordion-h .accordion__header.sc-cl-accordion:focus-visible::after{border:var(--border-width, 0.125rem) solid rgb(var(--system-color-focus, 136, 28, 248));border-radius:var(--border-radius-interactive-small, 0.875rem);content:\"\";inset:calc(-1 * var(--spacing-quarter, 0.125rem)) calc(-1 * var(--spacing-half, 0.25rem));position:absolute}.sc-cl-accordion-h .accordion__animation.sc-cl-accordion{padding-left:calc(var(--spacing-1, 0.5rem) + var(--spacing-half, 0.25))}.sc-cl-accordion-h .accordion__body.sc-cl-accordion{display:block;overflow:hidden}.sc-cl-accordion-h .accordion__icon.sc-cl-accordion{--icon-size:var(--accordion-chevron-size);--icon-color:rgb(var(--color-neutral-900, 20, 20, 20))}.sc-cl-accordion-h .accordion__content.sc-cl-accordion{border:var(--border-width, 0.125rem) solid transparent;height:100%;overflow:hidden;transition:var(--animation-duration-normal, 250ms);width:inherit}.sc-cl-accordion-h.accordion--disabled .sc-cl-accordion-s>[slot=content]{filter:grayscale(1) contrast(0.5) opacity(0.6)}.accordion--disabled.sc-cl-accordion-h .accordion__header.sc-cl-accordion{--accordion-chevron-color:rgba(var(--font-color-onlight-muted, 20, 20, 20, 0.5));--accordion-header-color:rgba(var(--font-color-onlight-muted, 20, 20, 20, 0.5));cursor:not-allowed}.accordion--disabled.sc-cl-accordion-h .accordion__body.sc-cl-accordion{position:relative}.accordion--disabled.sc-cl-accordion-h .accordion__body.sc-cl-accordion::after{content:\"\";cursor:not-allowed;height:100%;inset:0;opacity:0.5;position:absolute;width:100%}.accordion--expanded.sc-cl-accordion-h{padding-bottom:calc(3 * var(--accordion-spacing-top-bottom))}.accordion--divider.sc-cl-accordion-h{position:relative}.accordion--divider.sc-cl-accordion-h::before{border-bottom:var(--size-eighth, 0.0625rem) solid rgba(var(--color-neutral-500, 141, 141, 141), var(--opacity-4, 0.75));bottom:var(--accordion-spacing-top-bottom);content:\"\";position:absolute;width:100%}";

const ClAccordion$1 = /*@__PURE__*/ proxyCustomElement(class ClAccordion extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.clCollapseEnd = createEvent(this, "clCollapseEnd", 7);
    this.clCollapseStart = createEvent(this, "clCollapseStart", 7);
    this.clExpandEnd = createEvent(this, "clExpandEnd", 7);
    this.clExpandStart = createEvent(this, "clExpandStart", 7);
    this.collapseEnd = createEvent(this, "collapseEnd", 7);
    this.collapseStart = createEvent(this, "collapseStart", 7);
    this.expandEnd = createEvent(this, "expandEnd", 7);
    this.expandStart = createEvent(this, "expandStart", 7);
    /**
     * Object containing custom consoleLogger functions.
     */
    this.logger = createLogger(component);
    /**
     * Handler for the "collapse" method
     */
    this.bindCollapse = this.collapse.bind(this);
    /**
     * Handler for the "expand" method
     */
    this.bindExpand = this.expand.bind(this);
    /**
     * Handles collapsing/expanding the accordion element
     */
    this.handleToggle = (event) => {
      if (isTriggerEvent(event)) {
        event.preventDefault();
        if (!this.disabled) {
          this[this.expanded ? 'bindCollapse' : 'bindExpand']();
        }
      }
    };
    /**
     * Function that emits the correct animation event based on whether the accordion is expanding/collapsing
     */
    this.handleAnimationEvent = (event) => {
      if (!this.disabled) {
        const { type } = event;
        const isExpandEvent = this.expanded;
        switch (true) {
          case isExpandEvent && type === 'clAnimationStart':
            emitEvent(this, 'clExpandStart', { component, originalEvent: event }, null);
            break;
          case isExpandEvent && type === 'clAnimationEnd':
            emitEvent(this, 'clExpandEnd', { component, originalEvent: event }, null);
            break;
          case !isExpandEvent && type === 'clAnimationStart':
            emitEvent(this, 'clCollapseStart', { component, originalEvent: event }, null);
            break;
          case !isExpandEvent && type === 'clAnimationEnd':
            emitEvent(this, 'clCollapseEnd', { component, originalEvent: event }, null);
            break;
        }
      }
    };
    this.alignment = 'space-between';
    this.disabled = false;
    this.divider = false;
    this.expanded = false;
    this.primary = false;
  }
  /**
   * Validates the alignment property
   */
  handleAlignment(alignment) {
    if (!accordionValidator.alignment(alignment)) {
      this.logger.error.attribute.alignment(`Given invalid value "${alignment}".`);
    }
  }
  /**
   * Every time the component is connected to the DOM:
   * - Console log deprecation warnings;
   */
  connectedCallback() {
    logDeprecatedEventOnce(this.logger, 'collapseEnd', 'clCollapseEnd');
    logDeprecatedEventOnce(this.logger, 'collapseStart', 'clCollapseStart');
    logDeprecatedEventOnce(this.logger, 'expandEnd', 'clExpandEnd');
    logDeprecatedEventOnce(this.logger, 'expandStart', 'clExpandStart');
  }
  /**
   * Just once after the component has fully loaded and rendered:
   * - If the content should be expanded initially, force an update to set the correct expanded height;
   */
  componentDidLoad() {
    if (this.expanded && this.contentElement) {
      forceUpdate(this.element);
    }
  }
  /**
   * A method to collapse the accordion.
   */
  async collapse() {
    if (!this.disabled) {
      this.expanded = false;
    }
  }
  /**
   * A method to expand the accordion.
   */
  async expand() {
    if (!this.disabled) {
      this.expanded = true;
    }
  }
  render() {
    var _a, _b;
    const { alignment, disabled, divider, expanded, primary } = this;
    const animationDuration = +(getCSSValue('--ansimation-duration-fast') || '150ms').replace('ms', '');
    return (h(Host, { class: cl('accordion', {
        'accordion--disabled': disabled,
        'accordion--divider': divider,
        'accordion--expanded': expanded,
      }), tabIndex: -1 }, h("div", { class: cl(`accordion__header accordion__header--${alignment}`, {
        'accordion__header--primary': primary,
      }), tabIndex: disabled ? -1 : 0, onClick: this.handleToggle, onKeyPress: this.handleToggle }, h("slot", { name: 'label' }), h("slot", { name: 'subtext' }), h("cl-animation", { class: 'accordion__animation', duration: animationDuration, rotate: expanded ? '[0,0,180]' : '[0,0,0]', play: true }, h("cl-icon", { class: 'accordion__icon', name: 'navigation--chevron--down', slot: 'item' }))), h("cl-animation", { class: 'accordion__body', config: `{ "maxHeight" : ${expanded ? (_b = (_a = this.contentElement) === null || _a === void 0 ? void 0 : _a.offsetHeight) !== null && _b !== void 0 ? _b : 0 : 0}, "overflow": "hidden" }`, duration: animationDuration, play: true, onClAnimationEnd: this.handleAnimationEvent, onClAnimationStart: this.handleAnimationEvent }, h("div", { ref: el => {
        this.contentElement = el;
      }, class: 'accordion__content', slot: 'item' }, h("slot", { name: 'content' })))));
  }
  get element() { return this; }
  static get watchers() { return {
    "alignment": ["handleAlignment"]
  }; }
  static get style() { return clAccordionCss; }
}, [6, "cl-accordion", {
    "alignment": [1],
    "disabled": [4],
    "divider": [4],
    "expanded": [1028],
    "primary": [4],
    "collapse": [64],
    "expand": [64]
  }]);
function defineCustomElement$1() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["cl-accordion", "cl-animation", "cl-icon"];
  components.forEach(tagName => { switch (tagName) {
    case "cl-accordion":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, ClAccordion$1);
      }
      break;
    case "cl-animation":
      if (!customElements.get(tagName)) {
        defineCustomElement$3();
      }
      break;
    case "cl-icon":
      if (!customElements.get(tagName)) {
        defineCustomElement$2();
      }
      break;
  } });
}

const ClAccordion = ClAccordion$1;
const defineCustomElement = defineCustomElement$1;

export { ClAccordion, defineCustomElement };

//# sourceMappingURL=cl-accordion.js.map